// Eyelids, driven in code rather than keyed in the model.
//
// The rig carries lid.L and lid.R parented to head, and every clip keys them
// at a constant open pose because Blender writes the whole armature into
// every action. Those keys are harmless: the lids are written after the
// mixer has run, so whatever a clip says about them is overwritten. It does
// mean Sleep does not shut his eyes on its own, which is why closedness is
// computed here instead.
//
// Why not key blinks in the clips: a blink is state rather than
// choreography. It has to survive the body clip changing (he blinks whether
// walking, eating or watching), it has to answer to whether he is asleep,
// and two birds must never blink in step. None of that survives being baked
// into a walk cycle.

// Open to shut: a half turn about the lid bone's own Z.
//
// The bones run out sideways from the top of the head, one to each eye, and
// each lid is a cap over a round eye. Closing it is a roll about the axis
// through the eye rather than a hinge, which is why it is a full half turn
// and not the ninety-odd degrees a hinge would want.
//
// X, found by turning it while watching rather than by converting Blender
// axes on paper. The paper conversion said Z and rolled the lid the wrong
// way round the eye, which read as the blink happening vertically instead
// of across.
//
// The sign is not decorative even though the endpoints of a half turn are
// the same either way: it decides which way the lid sweeps on its way past.
export const LID_CLOSED_ANGLE = -Math.PI
export const LID_AXIS = 'x'

// A blink: shut fast, hold a moment, open slower. Real eyelids close in
// about 100ms and open in about 150, and copying that is most of what makes
// it read as a blink rather than a flicker.
const CLOSE_MS = 90
const HOLD_MS = 40
const OPEN_MS = 150
export const BLINK_MS = CLOSE_MS + HOLD_MS + OPEN_MS

/** How shut the lid is, 0..1, at `t` ms into a blink. */
export function blinkCurve(t) {
  if (t <= 0 || t >= BLINK_MS) return 0
  if (t < CLOSE_MS) return t / CLOSE_MS
  if (t < CLOSE_MS + HOLD_MS) return 1
  return 1 - (t - CLOSE_MS - HOLD_MS) / OPEN_MS
}

// Gap between spontaneous blinks. People blink every 2 to 10 seconds and the
// spacing is irregular; an exact interval reads as a metronome.
const GAP_MIN_MS = 1800
const GAP_MAX_MS = 6500

/** When the next blink should land, in ms from now. */
export function nextBlinkGap(rand = Math.random) {
  return GAP_MIN_MS + rand() * (GAP_MAX_MS - GAP_MIN_MS)
}

/**
 * How shut his eyes should sit before blinking is considered, 0..1.
 *
 * Asleep is shut. Otherwise it follows arousal, which is the same dial the
 * HUD draws as Calm and which now answers to weather and danger: a bird
 * dozing in the warm has heavier lids than one out in a storm.
 */
export function restingLid(action, arousal = 0) {
  if (action === 'SLEEP') return 1
  if (action === 'REST') return 0.35
  const a = Math.max(-1, Math.min(1, arousal || 0))
  // Deeply calm droops a little; keyed-up opens all the way.
  return a < 0 ? Math.min(0.4, -a * 0.4) : 0
}
